// Notification-center hooks — the WRAPPER layer (lint-exempt for TanStack Query). Notifications are
// PERSON-scoped (/me/**), never band-keyed: the keys live under ['me','notifications',…] so they
// survive a band switch (which scorches ['band', …]) — a notification is the person's, cross-band. The
// `notification-created` signal (ac.ui.user.{id}) invalidates the same keys via the invalidation registry,
// so the live badge/drawer and these hooks share one cache.
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { ApiError } from "./apiError.js";

export type NotificationRow = components["schemas"]["NotificationRow"];

/** The list query key (drawer). The registry's `notification-created` handler invalidates the parent
 * `['me','notifications']`, which covers this, the infinite key, and the unread key below. */
export const notificationsListKey = ["me", "notifications", "list"] as const;
export const notificationsInfiniteKey = ["me", "notifications", "list", "infinite"] as const;
export const notificationsUnreadKey = ["me", "notifications", "unread"] as const;

// Notifications are staleTime:0 — always considered stale, so a window focus / reconnect refetches
// server truth (the reconnect catch-up), while the `notification-created` signal invalidates them live.

/** `GET /me/notifications` — newest-first FIRST page of the caller's non-dismissed notifications (the drawer
 * quick-glance; the full page paginates via {@link useNotificationsInfinite}).*/
export function useNotifications() {
  const { api } = useApp();
  return useQuery({
    queryKey: notificationsListKey,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/me/notifications");
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    staleTime: 0,
  });
}

/** `GET /me/notifications?cursor` — the cursor-paginated full list for the mobile full page. Each
 * page is `{items, nextCursor}`; `getNextPageParam` stops when `nextCursor` is null. */
export function useNotificationsInfinite() {
  const { api } = useApp();
  return useInfiniteQuery({
    queryKey: notificationsInfiniteKey,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error, response } = await api.GET("/api/v1/me/notifications", {
        params: { query: pageParam ? { cursor: pageParam } : {} },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    // `|| undefined` (not `?? undefined`) so a null OR empty-string cursor both stop pagination — an empty
    // string would otherwise read as a truthy hasNextPage and loop "Load older" forever.
    getNextPageParam: (last) => last.nextCursor || undefined,
    staleTime: 0,
  });
}

/** `GET /me/notifications/unread-count` — the O(1) badge count.*/
export function useUnreadCount() {
  const { api } = useApp();
  return useQuery({
    queryKey: notificationsUnreadKey,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/me/notifications/unread-count");
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data.count;
    },
    staleTime: 0,
  });
}

// All read/dismiss mutations refetch the list + badge on settle so they agree with the server
// — invalidating the parent ['me','notifications'] key covers both the list and the unread children.
const invalidateKey = ["me", "notifications"];

export function useMarkRead() {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await api.POST("/api/v1/me/notifications/{id}/read", { params: { path: { id } } });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: invalidateKey }),
  });
}

export function useMarkAllRead() {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error, response } = await api.POST("/api/v1/me/notifications/read-all");
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: invalidateKey }),
  });
}

export function useDismiss() {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await api.POST("/api/v1/me/notifications/{id}/dismiss", { params: { path: { id } } });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: invalidateKey }),
  });
}

export function useDismissAll() {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error, response } = await api.POST("/api/v1/me/notifications/dismiss-all");
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: invalidateKey }),
  });
}
